import React, { useState, useEffect } from 'react';
import { Search, Globe, Brain, Sparkles, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ResearchLoadingAnimation({ onCancel }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);

  const steps = [
    {
      icon: Search,
      text: 'Web\'den güncel bilgiler toplanıyor',
      color: 'text-blue-500',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30'
    },
    {
      icon: Globe,
      text: 'Çoklu kaynaklardan veri çekiliyor',
      color: 'text-green-500',
      bgColor: 'bg-green-100 dark:bg-green-900/30'
    },
    {
      icon: Brain,
      text: 'AI ajanları paralel analiz yapıyor',
      color: 'text-purple-500',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30'
    },
    {
      icon: Sparkles,
      text: 'Bulgular sentezleniyor ve rapor hazırlanıyor',
      color: 'text-orange-500',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep((prev) => {
        const next = (prev + 1) % steps.length;
        if (next === 0) {
          // Reset completed steps when cycling
          setCompletedSteps([]);
        } else {
          setCompletedSteps((completed) => [...completed, prev]);
        }
        return next;
      });
    }, 2000); // Change step every 2 seconds

    return () => clearInterval(interval);
  }, []);

  // Track elapsed time
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleCancel = () => {
    if (onCancel) {
      onCancel(elapsedTime);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 rounded-lg p-6 border-2 border-blue-200 dark:border-blue-800">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div className="absolute inset-0 w-12 h-12 bg-blue-500 rounded-full animate-ping opacity-20"></div>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              🔬 Derin Araştırma Başlatıldı
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tüm AI ajanlarımız paralel çalışıyor...
            </p>
          </div>
        </div>
        
        {/* Cancel Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Araştırmayı iptal et"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="space-y-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = completedSteps.includes(index);

          return (
            <div
              key={index}
              className={`
                flex items-center space-x-3 p-3 rounded-lg transition-all duration-500
                ${isActive ? step.bgColor + ' scale-105' : 'bg-gray-50 dark:bg-gray-800/50'}
                ${isCompleted ? 'opacity-60' : 'opacity-100'}
              `}
            >
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center transition-all
                ${isActive ? step.color + ' bg-white dark:bg-gray-700 shadow-lg' : 'bg-gray-200 dark:bg-gray-700 text-gray-400'}
              `}>
                {isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Icon className={`w-5 h-5 ${isActive ? 'animate-bounce' : ''}`} />
                )}
              </div>
              <div className="flex-1">
                <p className={`
                  text-sm font-medium transition-colors
                  ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}
                `}>
                  {step.text}
                </p>
              </div>
              {isActive && (
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            ⏱️ Bu işlem 30-60 saniye sürebilir
          </span>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-600 dark:text-green-400 font-medium">
              {elapsedTime}s
            </span>
          </div>
        </div>
        {elapsedTime <= 5 && (
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
            💡 İlk 5 saniye içinde iptal ederseniz kredi iadesi yapılır
          </p>
        )}
      </div>
    </div>
  );
}
